import React, { useEffect, useState } from "react";
import { ConnectedProps } from "react-redux";
import { Button, Col, Form, Input, Row, Space, Spin, type UploadProps } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import DatesAndPackagePricing from "../Dashboard/DateAndPackagePricing";
import UploadPackageImages from "../Dashboard/UploadImage";
import connector from "./Container";

const Settings = (props: ConnectedProps<typeof connector>) => {
  const {
    state: {
      loggedUser,
      eventInfo,
      packagePricing,
      loading,
      eventImages,
      participants,
    },
    actions: {
      getEventInfo,
      getPackagePricing,
      addEventInfo,
      updateEventInfo,
      createPackagePricing,
      updatePackagePricing,
      getEventImagesById,
      addEventImage,
      resetDay,
      getParticipants,
    },
  } = props;

  const [loaded, setLoaded] = useState(false);
  const [isDisabledButton, setIsDisabledButton] = useState(true);
  const [fileListA, setFileListA] = useState<any>([]);
  const [fileListB, setFileListB] = useState<any>([]);
  const [fileListC, setFileListC] = useState<any>([]);
  const [fileListD, setFileListD] = useState<any>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    getEventInfo();
    getPackagePricing();
    getParticipants();
  }, []);

  useEffect(() => {
    loggedUser?.entityId && getEventImagesById(loggedUser?.entityId);
  }, [loggedUser]);

  useEffect(() => {
    if (!eventInfo?.length) return;

    form.setFieldsValue({
      VAT: eventInfo ? eventInfo[0].vat : null,
      firstDayDate: dayjs(eventInfo[0].firstDayDate),
      secondDayDate: dayjs(eventInfo[0].secondDayDate),
      priceType: eventInfo[0].priceType,
      gtmContainerId: eventInfo[0].gtmContainerId ?? "",
    });
  }, [eventInfo]);

  useEffect(() => {
    if (!eventImages?.images) return;

    setFileListA(
      eventImages?.images?.find((el: any) => el.package === "A") ?? []
    );
    setFileListB(
      eventImages?.images?.find((el: any) => el.package === "B") ?? []
    );
    setFileListC(
      eventImages?.images?.find((el: any) => el.package === "C") ?? []
    );
    setFileListD(
      eventImages?.images?.find((el: any) => el.package === "D") ?? []
    );
  }, [eventImages]);

  useEffect(() => {
    if (!packagePricing?.length) return;

    const packageA = packagePricing.find((el: any) => el.package === "A");
    const packageB = packagePricing.find((el: any) => el.package === "B");
    const packageC = packagePricing.find((el: any) => el.package === "C");
    const packageD = packagePricing.find((el: any) => el.package === "D");

    form.setFieldsValue({
      regularPriceKmA: packageA.price,
      regularPriceEurA: (packageA.price / 1.96).toFixed(2),
      earlyPriceKmA: packageA.earlyPrice,
      earlyPriceEurA: (packageA.earlyPrice / 1.96).toFixed(2),

      regularPriceKmB: packageB.price,
      regularPriceEurB: (packageB.price / 1.96).toFixed(2),
      earlyPriceKmB: packageB.earlyPrice,
      earlyPriceEurB: (packageB.earlyPrice / 1.96).toFixed(2),

      regularPriceKmC: packageC.price,
      regularPriceEurC: (packageC.price / 1.96).toFixed(2),
      earlyPriceKmC: packageC.earlyPrice,
      earlyPriceEurC: (packageC.earlyPrice / 1.96).toFixed(2),

      regularPriceKmD: packageD.price,
      regularPriceEurD: (packageD.price / 1.96).toFixed(2),
      earlyPriceKmD: packageD.earlyPrice,
      earlyPriceEurD: (packageD.earlyPrice / 1.96).toFixed(2),
    });
  }, [packagePricing]);

  useEffect(() => {
    if (eventInfo !== null && packagePricing !== null && eventImages !== null) {
      setLoaded(true);
      return;
    }
    setLoaded(false);
  }, [eventInfo, packagePricing, eventImages]);

  const onFinish = (values: any) => {
    //event
    if (eventInfo?.[0]?.id) {
      if (
        dayjs(values.firstDayDate).format("YYYY-MM-DD") !==
          dayjs(eventInfo[0].firstDayDate).format("YYYY-MM-DD") ||
        dayjs(values.secondDayDate).format("YYYY-MM-DD") !==
          dayjs(eventInfo[0].secondDayDate).format("YYYY-MM-DD") ||
        values.VAT !== eventInfo[0].vat ||
        values.priceType !== eventInfo[0].priceType ||
        (values.gtmContainerId ?? "") !== (eventInfo[0].gtmContainerId ?? "")
      ) {
        updateEventInfo(
          {
            vat: values.VAT,
            priceType: values.priceType,
            firstDayDate: dayjs(values.firstDayDate).format("YYYY-MM-DD"),
            secondDayDate: dayjs(values.secondDayDate).format("YYYY-MM-DD"),
            entityId: loggedUser?.entityId,
            gtmContainerId: values.gtmContainerId || null,
          },
          loggedUser?.entityId
        );
      }
    } else {
      addEventInfo({
        vat: values.VAT,
        priceType: values.priceType,
        firstDayDate: dayjs(values.firstDayDate).format("YYYY-MM-DD"),
        secondDayDate: dayjs(values.secondDayDate).format("YYYY-MM-DD"),
        entityId: loggedUser?.entityId,
      });
    }

    // package pricing
    const packageA = packagePricing.find((el: any) => el.package === "A");
    if (packageA?.id) {
      if (
        packageA.price != values.regularPriceKmA ||
        packageA.earlyPrice != values.earlyPriceKmA
      ) {
        updatePackagePricing(
          {
            package: "A",
            price: Number(values.regularPriceKmA),
            earlyPrice: Number(values.earlyPriceKmA),
            entityId: loggedUser?.entityId,
          },
          packageA?.id
        );
      }
    } else {
      createPackagePricing({
        package: "A",
        price: Number(values.regularPriceKmA),
        earlyPrice: Number(values.earlyPriceKmA),
        entityId: loggedUser?.entityId,
      });
    }

    const packageB = packagePricing.find((el: any) => el.package === "B");
    if (packageB?.id) {
      (packageB.price != values.regularPriceKmB ||
        packageB.earlyPrice != values.earlyPriceKmB) &&
        updatePackagePricing(
          {
            package: "B",
            price: Number(values.regularPriceKmB),
            earlyPrice: Number(values.earlyPriceKmB),
            entityId: loggedUser?.entityId,
          },
          packageB?.id
        );
    } else {
      createPackagePricing({
        package: "B",
        price: Number(values.regularPriceKmB),
        earlyPrice: Number(values.earlyPriceKmB),
        entityId: loggedUser?.entityId,
      });
    }

    const packageC = packagePricing.find((el: any) => el.package === "C");
    if (packageC?.id) {
      (packageC.price != values.regularPriceKmC ||
        packageC.earlyPrice != values.earlyPriceKmC) &&
        updatePackagePricing(
          {
            package: "C",
            price: Number(values.regularPriceKmC),
            earlyPrice: Number(values.earlyPriceKmC),
            entityId: loggedUser?.entityId,
          },
          packageC?.id
        );
    } else {
      createPackagePricing({
        package: "C",
        price: Number(values.regularPriceKmC),
        earlyPrice: Number(values.earlyPriceKmC),
        entityId: loggedUser?.entityId,
      });
    }

    const packageD = packagePricing.find((el: any) => el.package === "D");
    if (packageD?.id) {
      (packageD.price != values.regularPriceKmD ||
        packageD.earlyPrice != values.earlyPriceKmD) &&
        updatePackagePricing(
          {
            package: "D",
            price: Number(values.regularPriceKmD),
            earlyPrice: Number(values.earlyPriceKmD),
            entityId: loggedUser?.entityId,
          },
          packageD?.id
        );
    } else {
      createPackagePricing({
        package: "D",
        price: Number(values.regularPriceKmD),
        earlyPrice: Number(values.earlyPriceKmD),
        entityId: loggedUser?.entityId,
      });
    }

    // event package - image
    fileListA?.[0]?.originFileObj &&
      addEventImage(fileListA, loggedUser?.entityId, "A");
    fileListB?.[0]?.originFileObj &&
      addEventImage(fileListB, loggedUser?.entityId, "B");
    fileListC?.[0]?.originFileObj &&
      addEventImage(fileListC, loggedUser?.entityId, "C");
    fileListD?.[0]?.originFileObj &&
      addEventImage(fileListD, loggedUser?.entityId, "D");

    setIsDisabledButton(true);
  };

  const onValuesChange = (changedValues: any, values: any) => {
    setIsDisabledButton(false);
  };

  const onClickResetDay = () => {
    resetDay(loggedUser?.entityId);
  };

  return loaded ? (
    <Space direction="vertical" className="px-4 py-6 sm:p-8 w-full">
      <Form
        onValuesChange={onValuesChange}
        form={form}
        name="dateAndPackagePricing"
        onFinish={onFinish}
        layout="vertical"
        requiredMark={false}
      >
        <Row className="">
          <Col span={24} md={12} className="mb-6 md:mb-0">
            <DatesAndPackagePricing form={form} />
          </Col>
          <Col span={24} md={12} className="2xl:flex 2xl:justify-end">
            <div className="2xl:w-[90%]">
              <UploadPackageImages
                eventImage={eventImages}
                isUploading={loading}
                fileListA={fileListA}
                setFileListA={setFileListA}
                fileListB={fileListB}
                setFileListB={setFileListB}
                fileListC={fileListC}
                setFileListC={setFileListC}
                fileListD={fileListD}
                setFileListD={setFileListD}
                setIsDisabledButton={setIsDisabledButton}
              />
            </div>
          </Col>
        </Row>
        <div className="mt-6 mb-2">
          <div className="font-semibold text-sm mb-1">Google Tag Manager</div>
          <Form.Item
            name="gtmContainerId"
            label="GTM Container ID"
            extra="Format: GTM-XXXXXXX"
          >
            <Input placeholder="GTM-XXXXXXX" style={{ maxWidth: 280 }} />
          </Form.Item>
        </div>

        <div className="md:flex gap-8">
          <Form.Item>
            <Button
              disabled={isDisabledButton || loading}
              htmlType="submit"
              className="bg-custom-pink-900 text-white flex items-center justify-center font-medium text-base h-10 w-full md:w-[114px] rounded-sm border-custom-pink-900 shadow mb-4 md:mb-0"
            >
              {!loading ? (
                "SNIMI"
              ) : (
                <LoadingOutlined style={{ fontSize: 18 }} />
              )}
            </Button>
          </Form.Item>
          <Button
            onClick={onClickResetDay}
            disabled={
              loading ||
              participants.filter(
                (participant: any) =>
                  participant.attendanceDate &&
                  participant.status === "Prisutan"
              ).length === 0
            }
            htmlType="button"
            className="bg-custom-gray-800 text-custom-pink-900 flex items-center justify-center font-bold text-base h-10 w-full md:w-[140px] xl:w-[200px] rounded-sm border-2 border-custom-pink-900 shadow mb-12 md:mb-0"
          >
            RESETUJ DAN
          </Button>
        </div>
      </Form>
    </Space>
  ) : (
    <div className="w-full h-full flex justify-center items-center ">
      <Spin indicator={<LoadingOutlined style={{ fontSize: 46 }} spin />} />
    </div>
  );
};

export default Settings;
